import Vue from 'vue';
import dayjs from 'dayjs';
import i18n, { i18n_t } from '@/locales/i18n';
import {
  getFileIcon,
  isChinese,
  getDefaultColorByUserId,
  desensitive,
  isEmoji,
} from '@/renderer/scripts/utils/tools/index';
import { formatError } from '@/renderer/scripts/utils/errorCode/index';
import { httpUrl } from '@/renderer/scripts/http';
import { overCurrentTime, timeLeft } from '@/renderer/scripts/utils/dayjsUsage';
import { SMH } from '@/renderer/scripts/smh';
/**
 * 格式化大小
 */
Vue.filter('formatSize', (_size: number | string, unitSimple?: boolean) => {
  const size = +_size;
  if (Number.isNaN(size)) {
    return '-';
  }
  let sizeRes = parseFloat(String(size));
  let unit = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB'];
  if (unitSimple) {
    unit = ['B', 'K', 'M', 'G', 'T', 'P', 'E'];
  }
  if (!Number.isFinite(sizeRes)) {
    return 0 + unit[0];
  }
  let index = 0;
  for (; index < unit.length && sizeRes >= 1024; index += 1) {
    sizeRes /= 1024;
  }
  return `${(Math.round(sizeRes * 100) / 100).toFixed(1)} ${unit[index]}`;
});
Vue.filter('formatSpeed', (_size: number | string) => {
  const size = +_size;
  if (Number.isNaN(size)) {
    return '-';
  }
  let sizeRes = parseFloat(String(size));
  const unit = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB'];
  if (!Number.isFinite(sizeRes)) {
    return 0 + unit[0];
  }
  let index = 0;
  for (; index < unit.length && sizeRes >= 1024; index += 1) {
    sizeRes /= 1024;
  }
  return `${Math.round(sizeRes * 10) / 10} ${unit[index]}/s`;
});
Vue.filter('formatDate', (date: number | string, format = 'YYYY-MM-DD HH:mm:ss') => {
  if (dayjs(date).isValid()) {
    return dayjs(date).format(format);
  }
  return '-';
});
Vue.filter(
  'formatError',
  (
    error:
      | string
      | {
          code: string;
          interface?: string;
          message: string;
        },
    action: string,
  ) => {
    const errData = formatError(error, true);
    try {
      let msg = JSON.parse(errData);
      if (typeof msg === 'object' && msg !== null) {
        msg = i18n_t('未知错误');
      }
      switch (action) {
        case 'upload':
          msg = i18n_t('上传失败:{msg}', {
            msg,
          });
          break;
        case 'sync':
          msg = i18n_t('同步失败:{msg}', {
            msg,
          });
          break;
        case 'download':
          msg = i18n_t('下载失败:{msg}', {
            msg,
          });
          break;
        default:
          break;
      }
      return msg;
    } catch (err) {
      return errData;
    }
  },
);

// 获取文件后缀名（相应图标）
Vue.filter(
  'FileIcon',
  (
    filename: string,
    item: { type?: string; fileType?: string; isDir?: boolean; localSync?: any; hasSync?: boolean },
  ) => {
    if (item?.type === 'dir' || item?.isDir) {
      if ((item?.localSync && item.localSync.isSyncRootFolder) || item?.hasSync) {
        return 'sync-folder';
      }
      return 'folder';
    }
    return getFileIcon(filename, item?.fileType);
  },
);
Vue.filter('concatHttp', (val: string, overwrite = `${httpUrl.protocol}:`) => {
  if (!val) return '';
  return overwrite + val;
});
// 裁剪文字长度并显示省略号 （会将中文识别为 2 个字符）
Vue.filter('ellipsis', (val: string, num = 8) => {
  if (!val) return '';
  let len = 0;
  const strArr = [];
  for (const str of val) {
    if (isChinese(str)) len += 2;
    else if (isEmoji(str)) len += 2;
    else len += 1;
    if (len <= num) {
      strArr.push(str);
    } else {
      break;
    }
  }
  if (len <= num) {
    return val;
  }
  return `${strArr.join('')}...`;
});

/**
 * 当val占的字符小于num时: 直接返回字符串不做处理
 * 当val占的字符长度大于num时
 * 举例(截取后字符串长度比较接近):
 *  天若有情天亦老天若有情天亦老.txt --> 天若有情天..天亦老.txt
 *  abcdefghijklmnopqrstuvwxyz     --> abcdefghijk..pqrstuvwxyz
 *  😊😊😊😊😊😊😊😊😊😊.docx --> 😊😊😊😊😊..😊😊😊.docx
 */
Vue.filter('ellipsisName', (val: string, num = 24) => {
  if (!val) return '';
  let len = 0;
  const strArr = [];
  const numArr = [];
  // 使用for of循环不会把表情拆开成两个字符
  for (const str of val) {
    if (isChinese(str)) len += 2;
    else if (isEmoji(str)) len += 2;
    else len += 1;
    strArr.push(str);
    numArr.push(len);
  }
  if (len <= num) {
    return val;
  }
  const prefixNum = (num - 2) / 2;
  const suffixNum = (num - 2) / 2;
  let startPoint = 0;
  let endPoint = 0;
  for (let i = 0; i < numArr.length; i++) {
    if (numArr[i] <= prefixNum) {
      startPoint = i + 1;
    }
    if (numArr[numArr.length - 1] - numArr[i] >= suffixNum) {
      endPoint = i - numArr.length + 1;
    }
  }
  return `${strArr.slice(0, startPoint).join('')}..${strArr.slice(endPoint).join('')}`;
});

/**
 * 根据userId获取头像背景色和文字颜色或其他属性配置
 * @param {number} userId 用户Id
 * @param {string, string} otherConf 其他样式配置项obj
 * @returns obj
 */
Vue.filter('userStyleFilter', (userId: number, otherConf?: Record<string, string>) => {
  const colorObj = getDefaultColorByUserId(userId);
  const styleObj = {
    background: colorObj.bgColor,
    color: colorObj.textColor,
  };
  return otherConf ? Object.assign(styleObj, otherConf) : styleObj;
});
/**
 * 根据userNickName判断是否为中文 中文显示后几位 其他显示前几位
 * @param {string} userNickName 用户名
 * @param {number} num 显示的位数
 * @returns string
 */
Vue.filter('userNickNameFilter', (userNickName: string, num: number) => {
  if (!userNickName) return '';
  return isChinese(userNickName) ? userNickName.substr(-num) : userNickName.substr(0, num);
});

/**
 * 确定现在的时间还剩多少
 * @param {string} date 用户名
 * @param {boolean} needParentheses 是否显示括号
 * @returns string
 */
Vue.filter(
  'formDateToTimeString',
  (
    endTime: string,
    options: {
      disableParentheses: boolean;
      endStr: string;
      disableShowDay: boolean;
    } = {
      disableParentheses: false,
      endStr: i18n_t('结束'),
      disableShowDay: false,
    },
  ) => {
    if (overCurrentTime(new Date(), endTime)) return '';
    const { d, h, m } = timeLeft(new Date(), endTime);
    let str = '';
    if (d > 365) return str;
    if (d >= 1 && typeof options.disableShowDay !== 'undefined' && options.disableShowDay) {
      return str;
    }
    const end = options.endStr || i18n_t('结束');
    if (d > 1) {
      str = i18n_t('{d}天后{end}', {
        d,
        end,
      });
    } else if (d === 1 && h > 0) {
      str = i18n_t('{d}天{h}小时{m}分后{end}', {
        d,
        h,
        m,
        end,
      });
    } else if (h > 0) {
      str = i18n_t('{h}小时{m}分后{end}', {
        h,
        m,
        end,
      });
    } else if (m >= 1) {
      str = i18n_t('{m}分后{end}', {
        m,
        end,
      });
    }
    if (str && !options.disableParentheses) {
      str = `(${str})`;
    }
    return str;
  },
);

/**
 * 获取返回空间的完整路径
 * @param {item: SMH.SpaceFileItemData | SMH.SearchFileData}
 * @returns string
 */
Vue.filter('GetSpacePath', (item: SMH.SpaceFileItemData | SMH.SearchFileData) => {
  let spacePath = '';
  if (!item?.path) {
    return i18n_t('未知路径');
  }
  /**
   * 1、先根据isUserBelongSpace判断该条数据是否属于本空间内: 若为true则判断来源个人空间/企业空间/协作空间
   * 2、看relatedToMePath是否有值,有值则来源与我相关
   */
  if (item?.isUserBelongSpace) {
    if (item.user) {
      spacePath = i18n_t('个人空间/{slot1}', {
        slot1: item.path.join('/'),
      });
    } else if (item.team) {
      spacePath = i18n_t('企业空间/{slot1}/{slot2}', {
        slot1: item.team.name,
        slot2: item.path.join('/'),
      });
    } else if (item.group) {
      spacePath = i18n_t('协作空间/{slot1}/{slot2}', {
        slot1: item.group.name,
        slot2: item.path.join('/'),
      });
    }
  } else if (item?.relatedToMePath) {
    spacePath = i18n_t('与我相关/{slot1}', {
      slot1: item.path.join('/'),
    });
  }
  return spacePath;
});

/*
 * 字符串脱敏，abcdefg=a*****g
 * @param {string} str 字符串
 * @param {number} num 显示的位数
 * @returns string
 */
Vue.filter('desensitive', (str: string) => {
  return desensitive(str);
});
